import {
  detectAccountType,
  matchAccountTypeToTransferRefundType,
  resetAccountNumberAndCodeIfNeed,
  setAccountNumberAndCodeByType,
} from "@edaz/utils/bankAccount";
import { TransferRefundType } from "@edaz/utils/bankAccount/types";
import { DetailViewModel } from "@frui.ts/datascreens";
import { bound } from "@frui.ts/helpers";
import { attachAutomaticValidator, getValidationMessage, hasValidation, validate, validateAll } from "@frui.ts/validation";
import BankCodeType from "entities/bankCodeType";
import type ChangeFuelTypeWithoutRefundRequest from "entities/changeFuelTypeWithoutRefundRequest";
import type ChargeChangeResponse from "entities/chargeChangeResponse";
import type ChargeChangesDuringValidityItemResponse from "entities/chargeChangesDuringValidityItemResponse";
import type { IRefundCandidate } from "entities/chargeRefund";
import { RefundPaymentInformationValidationRulesFactory } from "entities/chargeRefund";
import type ChargeResponse from "entities/chargeResponse";
import {
  DomesticCounterpartyBankValidationRules,
  ForeignCounterpartyBankValidationRulesFactory,
  SepaCounterpartyBankValidationRules,
} from "entities/counterpartyBank";
import type CustomerRefundCandidateRequest from "entities/customerRefundCandidateRequest";
import type LicensePlate from "entities/licensePlate";
import type RefundTransferPayment from "entities/refundTransferPaymentResponse";
import TransferPaymentType from "entities/transferPaymentType";
import TransferRefundCounterpartyBankRequest from "entities/transferRefundCounterpartyBankRequest";
import TransferType from "entities/transferType";
import toUpper from "lodash/toUpper";
import { computed, observable, reaction } from "mobx";
import type ChargesRepository from "repositories/chargesRepository";
import type EnumService from "services/enumService";
import type EventBus from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type NotificationService from "services/notificationService";
import type OptionsService from "services/optionsService";
import type SecurityService from "services/securityService";
import type { EntityValidationRules } from "services/validationService";
import { removeInvisibleCharacters } from "utils/stringUtils";
import type AlertsViewModel from "../../../alertsViewModel";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import cloneDeep from "lodash/cloneDeep";
import type FuelTypeChangeRequest from "entities/fuelTypeChangeRequest";
import type CustomerPartialRefundCandidatePreviewResponse from "entities/customerPartialRefundCandidatePreviewResponse";

export abstract class BaseRefundViewModel<TEntity extends IRefundCandidate = IRefundCandidate> extends DetailViewModel<TEntity> {
  protected abstract readonly validationRules: EntityValidationRules<TEntity>;

  abstract onSave(): void;

  abstract translate(code: string, size?: number): string;

  abstract get paymentInformationOptional(): boolean;

  abstract get paymentInformationRequired(): boolean;

  abstract get paymentInformationDenied(): boolean;

  @observable fillBankAccount = false;

  @observable refundCandidate: TEntity;

  protected disposers: Array<() => void> = [];
  countrySelectViewModel: CountrySelectViewModel;

  @computed
  get isAllowedToChangePaymentInformation() {
    return this.securityService.allowTo("callcenter.chargeauditing", "write");
  }

  @computed
  get includePaymentOptions() {
    return (this.fillBankAccount && this.paymentInformationOptional) || this.paymentInformationRequired;
  }

  @computed
  get transferTypeOptions() {
    return this.optionsService.internalEnumOptions(TransferType).filter(item => item.id !== TransferType.Unspecified);
  }

  @computed
  get isSEPAPayment() {
    return this.item.transferType === TransferPaymentType.Sepa;
  }

  @computed
  get showSEPAWarning() {
    const countryId = this.item.counterpartyBank.countryId ?? "";
    const isSepaMember = this.enumService.value("countries", countryId)?.isSepaMember ?? false;

    return this.item.transferType === TransferPaymentType.Foreign && isSepaMember;
  }

  @computed
  get bankCodeOptions() {
    return this.optionsService.internalEnumOptions(BankCodeType);
  }

  @computed
  get showPaymentInformation() {
    return this.isAllowedToChangePaymentInformation && this.includePaymentOptions;
  }

  @computed
  get officialRecordId() {
    return this.item.officialRecordId;
  }

  set officialRecordId(recordNumber: string) {
    this.item.officialRecordId = toUpper(recordNumber);
  }

  constructor(
    protected repository: ChargesRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    protected securityService: SecurityService,
    protected enumService: EnumService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    public alertsViewModel: AlertsViewModel,
    public chargeResponse: ChargeResponse
  ) {
    super();
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  validate() {
    return this.includePaymentOptions ? validateAll([this.item, this.item.counterpartyBank]) : validate(this.item);
  }

  @bound
  attachValidators(itemReference?: TEntity) {
    const item = itemReference ?? this.item;
    let isErrorsVisible = false;
    if (hasValidation(item)) {
      isErrorsVisible = item.__validation.isErrorsVisible;
    }

    if (this.includePaymentOptions) {
      attachAutomaticValidator(
        item,
        { ...this.validationRules, ...RefundPaymentInformationValidationRulesFactory(item) },
        isErrorsVisible
      );
    } else {
      attachAutomaticValidator(item, this.validationRules, isErrorsVisible);
    }
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.refundCandidate = cloneDeep(this.item);
    this.disposers.push(reaction(() => this.fillBankAccount, this.onChangeFillBankAccount));
    this.disposers.push(
      reaction(() => this.item.transferType, this.onRefundPaymentTransferTypeChanged, { fireImmediately: true })
    );
    this.disposers.push(reaction(() => this.item.counterpartyBank?.codeType, this.onCodeTypeChanged));
    this.disposers.push(
      reaction(
        () => this.item.counterpartyCityAddress,
        counterpartyCityAddress => {
          if (counterpartyCityAddress) {
            this.item.counterpartyCityAddress = removeInvisibleCharacters(counterpartyCityAddress);
          }
        },
        { fireImmediately: true }
      )
    );
    this.item.emailAddress = this.chargeResponse.emailAddress ?? undefined;
  }

  async onDeactivate(isClosing: boolean) {
    const item = await this.loadDetail();
    if (item) {
      this.item = item;
    }

    if (isClosing) {
      this.disposers.forEach(dispose => dispose());
      this.disposers = [];
    }

    return super.onDeactivate(isClosing);
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @bound
  onInvalidAccountNumberPasted(value: string, event: ClipboardEvent) {
    const accountType = detectAccountType(value);
    const transferType = matchAccountTypeToTransferRefundType(accountType);

    if (transferType !== this.item.transferType) {
      this.item.transferType = transferType;
      if (!this.item.counterpartyBank) {
        this.item.counterpartyBank = new TransferRefundCounterpartyBankRequest();
      }
      setAccountNumberAndCodeByType(value, accountType, this.item.counterpartyBank);
      // We need to prevent default beacuse we're setting the input value above
      event.preventDefault();
    }
  }

  @bound
  private onRefundPaymentTransferTypeChanged(transferType?: TransferPaymentType | TransferRefundType) {
    const item = this.item;
    let isErrorsVisible = false;

    if (hasValidation(item)) {
      isErrorsVisible = item.__validation.isErrorsVisible;
    }

    if (transferType && transferType !== TransferPaymentType.Unspecified) {
      resetAccountNumberAndCodeIfNeed(this.item.counterpartyBank, transferType as any, this.item.counterpartyBank.codeType);
    }

    switch (transferType) {
      case TransferPaymentType.Foreign:
      case TransferRefundType.FOREIGN:
        attachAutomaticValidator(
          this.item.counterpartyBank,
          ForeignCounterpartyBankValidationRulesFactory(this.item.counterpartyBank),
          isErrorsVisible
        );
        break;
      case TransferPaymentType.Domestic:
      case TransferRefundType.DOMESTIC:
        attachAutomaticValidator(this.item.counterpartyBank, DomesticCounterpartyBankValidationRules, isErrorsVisible);
        break;
      case TransferPaymentType.Sepa:
      case TransferRefundType.SEPA:
        this.item.counterpartyBank.codeType = BankCodeType.Bic;
        attachAutomaticValidator(this.item.counterpartyBank, SepaCounterpartyBankValidationRules, isErrorsVisible);
        break;
    }
  }

  @bound
  private onChangeFillBankAccount() {
    if (!this.fillBankAccount) {
      this.refundCandidate.counterpartyBank = new TransferRefundCounterpartyBankRequest();
      this.refundCandidate.officialRecordId = this.item.officialRecordId;
      //TODO kontrola
      if (this.item.fuelTypeId) {
        this.refundCandidate.fuelTypeId = this.item.fuelTypeId;
      }
      this.setItem(this.refundCandidate);
    }
    this.attachValidators();
  }

  @bound
  private onCodeTypeChanged(codeType?: BankCodeType) {
    if (this.item.transferType && this.item.transferType !== TransferPaymentType.Unspecified) {
      resetAccountNumberAndCodeIfNeed(this.item.counterpartyBank, this.item.transferType as any, codeType);
    }
  }

  @bound
  officialRecordIdError() {
    return getValidationMessage(this.item, "officialRecordId");
  }

  translateAttribute(model: "licensePlate", attribute: keyof LicensePlate, size?: number): string;
  translateAttribute(
    model: "customerRefundCandidateRequest",
    attribute: keyof CustomerRefundCandidateRequest,
    size?: number
  ): string;
  translateAttribute(model: "fuelTypeChangeRequest", attribute: keyof FuelTypeChangeRequest, size?: number): string;
  translateAttribute(model: "charge", attribute: keyof ChargeResponse, size?: number): string;
  translateAttribute(model: "chargeChange", attribute: keyof ChargeChangeResponse, size?: number): string;
  translateAttribute(
    model: "chargeChangesDuringValidityItemResponse",
    attribute: keyof ChargeChangesDuringValidityItemResponse,
    size?: number
  ): string;
  translateAttribute(model: "transferRefundPayment", attribute: keyof RefundTransferPayment, size?: number): string;
  translateAttribute(
    model: "transferRefundCounterpartyBankRequest",
    attribute: keyof TransferRefundCounterpartyBankRequest,
    size?: number
  ): string;
  translateAttribute(
    model: "customerPartialRefundCandidatePreviewResponse",
    attribute: keyof CustomerPartialRefundCandidatePreviewResponse,
    size?: number
  ): string;
  translateAttribute(
    // tslint:disable:max-union-size
    model:
      | "licensePlate"
      | "customerRefundCandidateRequest"
      | "changeFuelTypeWithoutRefundRequest"
      | "charge"
      | "chargeChange"
      | "chargeChangesDuringValidityItemResponse"
      | "transferRefundPayment"
      | "transferRefundCounterpartyBankRequest"
      | "fuelTypeChangeRequest"
      | "customerPartialRefundCandidatePreviewResponse",
    attribute:
      | keyof LicensePlate
      | keyof CustomerRefundCandidateRequest
      | keyof ChangeFuelTypeWithoutRefundRequest
      | keyof ChargeResponse
      | keyof ChargeChangeResponse
      | keyof ChargeChangesDuringValidityItemResponse
      | keyof RefundTransferPayment
      | keyof TransferRefundCounterpartyBankRequest
      | keyof FuelTypeChangeRequest
      | keyof CustomerPartialRefundCandidatePreviewResponse,
    // tslint:enable:max-union-size
    size?: number
  ) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }
}
